import axios from "axios";
import Cookies from "js-cookie";
import { decrypt } from "../../_utilities/_encryptDecryptHelper";
import {
  getAttributes,
  getUserDetails,
  GetUserId,
  GetUserInfo,
} from "../../_utilities/getUserInfo";
import { getAccessToken } from "../../_utilities/utilities";
import { authService } from "../../api/authApi";
import { SESSIONEXPIRED_MSG } from "../../_utilities/message";

export const SESSION_CONFLICT_EVENT = "hirewing:session-conflict";

let sessionConflictDispatched = false;

function handleSessionConflict(error) {
  if (!sessionConflictDispatched) {
    sessionConflictDispatched = true;
    const message = error?.response?.data?.message || "";
    window.dispatchEvent(
      new CustomEvent(SESSION_CONFLICT_EVENT, { detail: { message } }),
    );
  }
  // Never resolve: prevents caller reducers/snackbars from firing while the
  // modal is up and navigation is pending. Page unloads on user logout click.
  return new Promise(() => {});
}

// NOTE: Don't call GetUserId() at module initialization - it causes circular dependency
// These values are checked inside each API function instead

var headers = {
  PortalType: "atsportal",
  "Content-Type": "application/json",
  // 'Access-Control-Allow-Origin': '*',
};

var axiosApi = axios.create({
  baseURL: process.env.REACT_APP_JOBOPENING,
  headers: headers,
  timeout: 2500,
  withCredentials: true,
});

axiosApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error),
);

export function sessionExpired(msg) {
  Cookies.remove("selectCountry");
  Cookies.remove("selectCountryId");
  Cookies.remove("AccessToken");
  Cookies.remove("RefreshToken");
  localStorage.clear();
  sessionStorage.clear();
  sessionStorage.setItem("sessionExpiredMsg", SESSIONEXPIRED_MSG);
  window.location.assign("/ats/login");
}

export function errorToasMessage(msg) {
  const toast = document.createElement("div");
  toast.className = "customtoast";
  const icon = document.createElement("div");
  icon.className = "icon";
  icon.textContent = "!";
  toast.appendChild(icon);
  toast.append(msg);
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.remove();
  }, 4000);
}

// Get method
export async function getApi(url, baseurl, customHeaders = {}, options = {}) {
  try {
    const userId = await GetUserId();
    const countryId = JSON.parse(decrypt("selectCountryId"));
    const userName = JSON.parse(decrypt("GetUserName"));
    const userDetails = await GetUserInfo();
    const adminDetails = await getAttributes();
    // const token = await getAccessToken();

    const requestHeaders = {
      CustomerId: userId,
      CountryId: countryId,
      CustomerName: userName,
      "Content-Type": "application/json",
      // ...(token && { Authorization: `Bearer ${token}` }),
      ...customHeaders,
    };

    if (userDetails?.length && adminDetails?.length) {
      requestHeaders.adminId = userDetails[0]?.id;
      requestHeaders.UserId = adminDetails[0]?.userId;
    }

    const axiosInstance = baseurl ? axios : axiosApi;
    const finalUrl = baseurl ? `${baseurl}${url}` : url;

    const noCredentialsPaths = [
      "/admin/mgmt",
      "/cloudsourcing/mgmt",
      "/jobboardportal/",
    ];
    const enableCredentials = noCredentialsPaths.some((path) =>
      finalUrl.includes(path),
    );
    const isAdminAPI = finalUrl.includes("atsAdmin=true");

    const response = await axiosInstance.get(finalUrl, {
      headers: requestHeaders,
      withCredentials: isAdminAPI || !enableCredentials,
      cancelToken: options?.cancelToken,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 498) {
      return handleSessionConflict(error);
    }
    if (
      error.response?.status === 405 ||
      error.response?.status === 406 ||
      error.response?.status === 401
    ) {
      return sessionExpired(error.response?.data?.message);
    }
    console.error("API Error:", error.response?.status, error.response?.data);
    throw error;
  }
}

// Post method
export async function postApi(url, data, baseurl, isFormData, jobObj) {
  try {
    // const token = getAccessToken();
    const userInfo = await getUserDetails();
    const userId = await GetUserId();
    const countryId = JSON.parse(decrypt("selectCountryId"));
    const userName = JSON.parse(decrypt("GetUserName"));

    let requestHeaders = {
      CustomerId: userId,
      CountryId: countryId,
      CustomerName: userName,
      // ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    };

    // Attach Job Data if present
    if (jobObj) {
      requestHeaders.Jobid = jobObj.jobId;
      requestHeaders.Jobcode = jobObj.jobCode;
      if (jobObj.userId) {
        requestHeaders.UserId = jobObj.userId;
      }
    }

    // Add userId for non-forgot password flows
    if (
      !["/forgot_password", "/verify_otp", "/generate_password"].includes(
        url,
      ) &&
      !isFormData
    ) {
      data['userId'] = userInfo?.userId || "";
    }

    const axiosInstance = baseurl ? axios : axiosApi;
    const finalUrl = baseurl ? `${baseurl}${url}` : url;

    const noCredentialsPaths = [
      "/admin/mgmt",
      "/cloudsourcing/mgmt",
      "/jobboardportal/",
    ];
    const enableCredentials = noCredentialsPaths.some((path) =>
      finalUrl.includes(path),
    );
    const isAdminAPI = finalUrl.includes("atsAdmin=true");

    const response = await axiosInstance.post(finalUrl, data, {
      headers: requestHeaders,
      withCredentials: isAdminAPI || !enableCredentials,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 498) {
      return handleSessionConflict(error);
    }
    if (error.response?.status === 422) {
      return error.response;
    }
    if (
      error.response?.status === 405 ||
      error.response?.status === 406 ||
      error.response?.status === 401
    ) {
      return sessionExpired(error.response?.data?.message);
    } else if (error.response?.status === 413) {
      return errorToasMessage(error.response?.data?.message);
    }
    if (url.includes("unsubscribe")) {
      return error.response || { status: 400, data: { message: error.message } };
    }
    throw error;
  }
}

// Put method
export async function putApi(url, data, baseurl, isFormData) {
  try {
    // const token = getAccessToken();
    const userInfo = await getUserDetails();
    const userId = await GetUserId();
    const countryId = JSON.parse(decrypt("selectCountryId"));
    const userName = JSON.parse(decrypt("GetUserName"));

    data.userId = userInfo.userId;

    const requestHeaders = {
      CustomerId: userId,
      CountryId: countryId,
      CustomerName: userName,
      // ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    };
    if (url.includes("portals")) {
      requestHeaders.userId = userInfo.userId;
    }

    const axiosInstance = baseurl ? axios : axiosApi;
    const finalUrl = baseurl ? `${baseurl}${url}` : url;

    const noCredentialsPaths = [
      "/admin/mgmt",
      "/cloudsourcing/mgmt",
      "/jobboardportal/",
    ];
    const enableCredentials = noCredentialsPaths.some((path) =>
      finalUrl.includes(path),
    );
    const isAdminAPI = finalUrl.includes("atsAdmin=true");

    const response = await axiosInstance.put(finalUrl, data, {
      headers: requestHeaders,
      withCredentials: isAdminAPI || !enableCredentials,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 498) {
      return handleSessionConflict(error);
    }
    if (
      error.response?.status === 405 ||
      error.response?.status === 406 ||
      error.response?.status === 401
    ) {
      return sessionExpired(error.response?.data?.message);
    }
    throw error;
  }
}

// Patch method
export async function patchApi(url, data, baseurl, isFormData) {
  try {
    // const token = getAccessToken();
    const userInfo = await getUserDetails();
    const userId = await GetUserId();
    const countryId = JSON.parse(decrypt("selectCountryId"));
    const userName = JSON.parse(decrypt("GetUserName"));

    data.userId = userInfo.userId;

    const requestHeaders = {
      CustomerId: userId,
      CountryId: countryId,
      CustomerName: userName,
      // ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    };

    const axiosInstance = baseurl ? axios : axiosApi;
    const finalUrl = baseurl ? `${baseurl}${url}` : url;

    const noCredentialsPaths = [
      "/admin/mgmt",
      "/cloudsourcing/mgmt",
      "/jobboardportal/",
    ];
    const enableCredentials = noCredentialsPaths.some((path) =>
      finalUrl.includes(path),
    );
    const isAdminAPI = finalUrl.includes("atsAdmin=true");

    const response = await axiosInstance.patch(finalUrl, data, {
      headers: requestHeaders,
      withCredentials: isAdminAPI || !enableCredentials,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 498) {
      return handleSessionConflict(error);
    }
    if (
      error.response?.status === 405 ||
      error.response?.status === 406 ||
      error.response?.status === 401
    ) {
      return sessionExpired(error.response?.data?.message);
    }
    throw error;
  }
}

// Delete method
export async function deleteApi(url, data, baseurl, customHeaders = {}) {
  try {
    // const token = await getAccessToken();
    const userInfo = await getUserDetails();
    const userId = await GetUserId();
    const countryId = JSON.parse(decrypt("selectCountryId"));
    const userName = JSON.parse(decrypt("GetUserName"));
    const userDetails = await GetUserInfo();
    const adminDetails = await getAttributes();

    const requestHeaders = {
      CustomerId: userId,
      CountryId: countryId,
      CustomerName: userName,
      "Content-Type": "application/json",
      // ...(token && { Authorization: `Bearer ${token}` }),
      ...customHeaders,
    };

    data.userId = userInfo.userId;
    data.customerId = userId;
    data.CountryId = countryId;
    if (userDetails?.length && adminDetails?.length) {
      requestHeaders.adminId = userDetails[0]?.id;
      requestHeaders.UserId = adminDetails[0]?.userId;
    }

    const axiosInstance = baseurl ? axios : axiosApi;
    const finalUrl = baseurl ? `${baseurl}${url}` : url;

    const noCredentialsPaths = [
      "/admin/mgmt",
      "/cloudsourcing/mgmt",
      "/jobboardportal/",
    ];
    const enableCredentials = noCredentialsPaths.some((path) =>
      finalUrl.includes(path),
    );
    const isAdminAPI = finalUrl.includes("atsAdmin=true");

    const response = await axiosInstance.delete(finalUrl, {
      data,
      headers: requestHeaders,
      withCredentials: isAdminAPI || !enableCredentials,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 498) {
      return handleSessionConflict(error);
    }
    if (
      error.response?.status === 405 ||
      error.response?.status === 406 ||
      error.response?.status === 401
    ) {
      return sessionExpired(error.response?.data?.message);
    }
    throw error;
  }
}
